import React, { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import FormSelect from "../common/FormSelect";
import { DropDownBold } from "../AdminIcons";
import { useCreateRoleMutation } from "../../services/user/userApi";
import { buildRoutePermissionPayload } from "../../utils/rolePermissionMapper";
import { permissionOptions } from "../../utils/rolePermissionOptions";

const CreateRoleModal = ({ isOpen, onClose, onSuccess }) => {
  const [apiError, setApiError] = useState("");
  const [selectedPermissions, setSelectedPermissions] = useState({});
  const [createRole, { isLoading }] = useCreateRoleMutation();

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors, isSubmitting },
  } = useForm({
    mode: "onSubmit",
    defaultValues: {
      name: "",
      description: "",
    },
  });

  const handleClose = () => {
    reset();
    setApiError("");
    setSelectedPermissions({});
    onClose();
  };

  useEffect(() => {
    if (!isOpen) {
      return undefined;
    }

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, [isOpen]);

  const onSubmit = async (formValues) => {
    try {
      setApiError("");

      const routePermissionArray = buildRoutePermissionPayload(selectedPermissions);

      if (routePermissionArray.length === 0) {
        setApiError("Please select at least one permission");
        return;
      }

      const payload = {
        name: formValues.name.trim(),
        description: formValues.description.trim(),
        routePermission: routePermissionArray,
      };

      await createRole(payload).unwrap();
      await onSuccess?.();
      handleClose();
    } catch (error) {
      setApiError(error?.data?.message || "Unable to create role. Please try again.");
    }
  };

  if (!isOpen) {
    return null;
  }

  return (
    <div
      className="fixed inset-0 z-[70] flex items-center justify-center overflow-hidden bg-black/45 p-6 sm:p-10"
      onClick={handleClose}
    >
      <div
        className="max-h-[min(94vh,calc(100dvh-1.5rem))] w-full max-w-[570px] overflow-y-auto rounded-2xl border border-[#00000014] bg-[#EAE9E9] [scrollbar-width:none] [-ms-overflow-style:none] [&::-webkit-scrollbar]:hidden"
        onClick={(event) => event.stopPropagation()}
      >
        <div className="px-4 pb-5 pt-6 sm:px-6 sm:pb-7 sm:pt-8 lg:px-12">
          <h3 className="text-lg font-medium leading-6 text-black sm:text-xl">Create a Role</h3>

          <form className="mt-5 space-y-5 sm:space-y-6" noValidate onSubmit={handleSubmit(onSubmit)}>
            <div>
              <label className="mb-1.5 block text-black">Enter Role Name</label>
              <input
                type="text"
                className="h-11 w-full rounded-[5px] border border-[#00000014] bg-[#D9D9D961] px-4 text-base text-black outline-none sm:text-xl"
                placeholder="e.g., Verifier"
                {...register("name", {
                  required: "Role name is required",
                })}
              />
              {errors.name?.message ? (
                <p className="mt-1.5 text-sm text-[#DC2626]">{errors.name.message}</p>
              ) : null}
            </div>

            <div>
              <label className="mb-1.5 block text-black">Role Description</label>
              <textarea
                rows={4}
                className={`w-full rounded-[5px] border bg-[#D9D9D961] px-4 py-3 text-base text-black outline-none sm:text-xl ${
                  errors.description?.message
                    ? "border-[#DC2626] focus:ring-1 focus:ring-[#DC2626]"
                    : "border-[#00000014]"
                }`}
                placeholder="e.g., Verify any account"
                {...register("description", {
                  required: "Description is required",
                  minLength: {
                    value: 10,
                    message: "Description must be at least 10 characters",
                  },
                  maxLength: {
                    value: 300,
                    message: "Description must not exceed 300 characters",
                  },
                })}
              />
              {errors.description?.message ? (
                <p className="mt-1.5 text-sm text-[#DC2626]">{errors.description.message}</p>
              ) : null}
            </div>

            <div>
              <label className="mb-1.5 block text-black">Select Permissions</label>
              <FormSelect
                id="create-role-permission"
                options={permissionOptions}
                defaultValue=""
                placeholder=""
                controlWrapperClassName="mt-0"
                selectClassName="h-11 rounded-[5px] border border-[#00000014] bg-[#D9D9D961] px-4 text-base text-black sm:text-xl"
                dropdownClassName="left-0 max-h-[45vh] w-full overflow-y-auto py-3 text-black font-medium sm:h-80 sm:max-h-80"
                icon={DropDownBold}
                iconProps={{ color: "#3C3C3C" }}
                optionIcon={DropDownBold}
                optionIconProps={{ color: "#3C3C3C" }}
                optionClassName="px-4 py-2"
                showTotalSelectedCount
                showPerOptionSelectedCount={false}
                onSubOptionsChange={(selectedSubOptions) => {
                  setSelectedPermissions(selectedSubOptions);
                }}
              />
            </div>

            {apiError ? (
              <p className="text-sm font-medium text-[#DC2626]">{apiError}</p>
            ) : null}
          </form>

          <div className="mt-8 flex flex-col-reverse gap-3 sm:mt-10 sm:flex-row sm:justify-end sm:gap-5">
            <button
              type="button"
              className="h-10 rounded-lg bg-[#DBDBDB] px-6 text-base font-medium text-[#686868] sm:h-8 sm:text-lg"
              onClick={handleClose}
            >
              Cancel
            </button>
            <button
              type="button"
              disabled={isSubmitting || isLoading}
              className="h-10 rounded-lg bg-[#923D8C] px-5 text-base font-medium text-[#FAFCFE] disabled:cursor-not-allowed disabled:opacity-70 sm:h-8 sm:text-lg"
              onClick={handleSubmit(onSubmit)}
            >
              {isSubmitting || isLoading ? "Creating..." : "Create Role"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CreateRoleModal;
